import type { IconDefinition } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { chainIds, IS_PRODUCTION_DEPLOYMENT } from '@soundxyz/utils/const';
import { getContractsForChain } from '@soundxyz/utils/constants/contract-addresses';
import type {
  AllowlistArtistFragment,
  AllowlistReleaseFragment,
  MintingAccessConfigMintingType,
  SelectedArtistInfoFragment,
  SelectedReleaseInfoFragment,
} from '@graphql/reactQuery';
import { MAX_UPLOAD_RELEASE_PRICE_DECIMALS } from '@utils/helpers';
import type { ReleaseForAllowlist } from '../Upload/AuctionConfiguration/AllowlistForm/Upload/useReleasesForAllowlist';
export const NEW_RELEASE_TITLE = 'New Release';

export const MAX_CHAR_LENGTH = {
  presaleAllowList: {
    description: 144,
  },
} as const;

export const MAX_DECIMAL_ERROR = `Price must be fewer than ${
  MAX_UPLOAD_RELEASE_PRICE_DECIMALS + 1
} decimals`;

export enum AuctionTypeEnum {
  OPEN_EDITION = 'OPEN_EDITION',
  FIXED_QUANTITY = 'FIXED_QUANTITY',
}

export const Collectors = {
  RELEASES: 'RELEASES',
  ARTISTS: 'ARTISTS',
} as const;

export type CollectorsType = keyof typeof Collectors;
export type MintingType =
  (typeof MintingAccessConfigMintingType)[keyof typeof MintingAccessConfigMintingType];

export enum MultiCategory {
  AUTHORED_RELEASE = 'AUTHORED_RELEASE',
  COLLAB_RELEASE = 'COLLAB_RELEASE',
  RELEASE = 'RELEASE',
  ARTIST = 'ARTIST',
  ARTIST_COLLECTORS = 'ARTIST_COLLECTORS',
}

export const MultiRow = {
  SINGLE: 'SINGLE',
  ALL: 'ALL',
} as const;

export const QueryType = {
  ARTIST_SEARCH: 'ARTIST_SEARCH',
  ARTIST_SELECTED: 'ARTIST_SELECTED',
  RELEASE_SEARCH: 'RELEASE_SEARCH',
  RELEASE_SELECTED: 'RELEASE_SELECTED',
  RELEASE_PAGINATED: 'RELEASE_PAGINATED',
} as const;

type ItemBase = {
  id: string;
};

export type MultiSelectSingleItem = ItemBase & {
  rowType: 'SINGLE';
  category: 'AUTHORED_RELEASE' | 'COLLAB_RELEASE' | 'RELEASE' | 'ARTIST' | 'ARTIST_COLLECTORS';
} & (
    | {
        queryType: typeof QueryType.RELEASE_PAGINATED;
        fragment: ReleaseForAllowlist;
      }
    | {
        queryType: typeof QueryType.RELEASE_SEARCH;
        fragment: AllowlistReleaseFragment;
      }
    | {
        queryType: typeof QueryType.RELEASE_SELECTED;
        fragment: SelectedReleaseInfoFragment;
      }
    | {
        queryType: typeof QueryType.ARTIST_SEARCH;
        fragment: AllowlistArtistFragment;
      }
    | {
        queryType: typeof QueryType.ARTIST_SELECTED;
        fragment: SelectedArtistInfoFragment;
      }
  );

export type MultiSelectAllItem = ItemBase & {
  rowType: 'ALL';
  text: string;
  icon?: IconDefinition;
  avatarUrl?: string;
  category: keyof typeof MultiCategory;
};

export type MultiSelectItem = MultiSelectSingleItem | MultiSelectAllItem;

export const EDITION_COVER_SIZE = {
  FINAL: 552,
  REVIEW: 65,
  SELECTOR: 117,
} as const;

export const EDITION_COVER_WRAPPER_SIZE = {
  FINAL: 800,
  REVIEW: 96,
  SELECTOR: 158,
} as const;

export const EDITION_COVER_BORDER_SIZE = {
  FINAL: 14,
  REVIEW: 2,
  SELECTOR: 4,
} as const;

export const PRESALE_COPY = 'Give specific collectors early access and/or special pricing.';

export const EDITION_COVER_VIP_TOOLTIP =
  'Limited Editions are scarce and only available based on your selected drop type (open or fixed edition).';
export const EDITION_COVER_GOLDEN_EGG_TOOLTIP =
  "The Golden Egg is a special 1-of-1 artwork that is randomly rewarded after the sale ends. The winner's artwork is then upgraded to the Golden Egg art.";
export const EDITION_COVER_GA_TOOLTIP =
  'Forever Editions are unlimited and open forever. Audio not included. Collectors pay an artist and Sound fee per edition. Available to collect when public sale starts';

export const EDITION_COVER_GA_COPY =
  'Unlimited and open forever, available when Limited Edition public sale starts. You earn 100% of the Artist Fee for each edition. Audio not included.';
export const EDITION_COVER_VIP_COPY =
  'A Limited Edition gives collectors priority seating and exclusive badging. You set price and quantity/duration in the drop configuration section.';
export const EDITION_COVER_GOLDEN_EGG_COPY =
  'A special 1-of-1 artwork that is randomly rewarded to a Limited Edition collector after the sale ends. The winner’s artwork is then upgraded to this Golden Egg Edition.';

export const SONG_INFO_SUBTEXT_COPY =
  'Upload your song, artwork, and add your song info, which will be displayed on your release page.';
export const CREDITS_AND_SPLITS_SUBTEXT_COPY =
  'Add collaborators to distribute earnings based on splits. All collaborators can withdraw shares on Sound.xyz with their wallet.';

export const EDITION_COVER_GA_GENERATE_ID = 'edition-cover-ga-to-generation';
export const EDITION_COVER_GOLDEN_EGG_GENERATE_ID = 'edition-cover-golden-egg-to-generation';

export const VIP_TITLE = 'Limited Edition';
export const VIP_COPY =
  'A Limited Edition gives collectors priority seating and exclusive badging. Price and quantity is set by you in the drop configuration section.';

export const GOLDEN_EGG_TITLE = 'Golden Egg Edition';
export const GOLDEN_EGG_COPY =
  'A special 1-of-1 artwork that is randomly rewarded to a Limited Edition collector after the sale ends.';

export const GA_TITLE = 'Forever Edition';
export const GA_COPY =
  "A patronage tier that's unlimited and open forever, available when your Limited Edition public sale starts. You earn an Artist Mint fee for each edition.";

export const SPOTIFY_PRESAVE_TITLE = 'Spotify Presaves';
export const SPOTIFY_PRESAVE_COPY =
  'Create hype for your upcoming release by allowing collectors to presave your song on Spotify to claim a free Forever Edition on Sound.';
export const SPOTIFY_PRESAVE_DATE_COPY =
  'Your start time must be the same date of your Spotify release.';
export const SPOTIFY_PRESAVE_DISABLED_ETH_COPY =
  'Spotify Presaves are only enabled for Optimism. Disable it to continue on Ethereum.';

export const COVER_STEP_3_TOOLTIP = 'This cover can be edited in the Release Info step';

const OPTIMISM_CONTRACTS = IS_PRODUCTION_DEPLOYMENT
  ? getContractsForChain('OPTIMISM')
  : getContractsForChain('OPTIMISM_GOERLI');
const MAINNET_CONTRACTS = IS_PRODUCTION_DEPLOYMENT
  ? getContractsForChain('MAINNET')
  : getContractsForChain('GOERLI');
const BASE_CONTRACTS = IS_PRODUCTION_DEPLOYMENT
  ? getContractsForChain('BASE')
  : getContractsForChain('BASE_GOERLI');

export function getSoundContractAddressesConstants(chainId: number) {
  switch (chainId) {
    case chainIds.GOERLI:
    case chainIds.MAINNET:
      return MAINNET_CONTRACTS;
    case chainIds.OPTIMISM_GOERLI:
    case chainIds.OPTIMISM:
      return OPTIMISM_CONTRACTS;
    case chainIds.BASE:
    case chainIds.BASE_GOERLI:
      return BASE_CONTRACTS;
    default:
      throw new Error(`Invalid chainId ${chainId}`);
  }
}
